/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import {
	Component,
	computed,
	inject,
	OnDestroy,
	OnInit,
	signal,
	TemplateRef,
	ViewChild
} from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { EChartsOption, EChartsType } from "echarts";
import {
	BehaviorSubject,
	catchError,
	defer,
	EMPTY,
	filter,
	finalize,
	forkJoin,
	map,
	Observable,
	Subject,
	switchMap,
	take,
	takeUntil,
	tap,
	throwError,
	timer
} from "rxjs";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { ChartComponent } from "../../shared/components/chart/chart.component";
import { DropdownComponent } from "../../shared/components/dropdown/dropdown.component";
import { InputComponent } from "../../shared/components/input/input.component";
import { KpiComponent } from "../../shared/components/kpi/kpi.component";
import { SpinnerComponent } from "../../shared/components/spinner/spinner.component";
import { SwitchComponent } from "../../shared/components/switch/switch.component";
import { PaginationComponent } from "../../shared/components/table/pagination/pagination.component";
import { TableComponent } from "../../shared/components/table/table.component";
import { UploadFileComponent } from "../../shared/components/upload-file/upload-file.component";
import { EnvironmentalImpactByApplication } from "../../shared/models/http/application/environmental-impact-by-application.model";
import { BarChart } from "../../shared/models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../shared/models/http/charts/line-chart/line-chart.model";
import { PieChart } from "../../shared/models/http/charts/pie-chart/pie-chart.model";
import { StrategicApplicationFileStatusResponse } from "../../shared/models/http/strategic-applications/strategic-application-file-status-response.model";
import { StrategicApplicationKpi } from "../../shared/models/http/strategic-applications/strategic-application-kpi.model";
import { RolePermission } from "../../shared/models/ui/authorization.model";
import { ChartResponse } from "../../shared/models/ui/charts.model";
import { ReportingPeriodValue } from "../../shared/models/ui/reporting-period.model";
import { SelectOption } from "../../shared/models/ui/select-option.model";
import { AuthorizationService } from "../../shared/services/authorization.service";
import { DialogService } from "../../shared/services/dialog.service";
import { StorageService } from "../../shared/services/storage.service";
import { StrategicApplicationsService } from "../../shared/services/strategic-applications.service";
import { ToastService } from "../../shared/services/toast.service";
import { COLORS, PATHS } from "../../shared/utils/constants";
import { GRANTS } from "../../shared/utils/grants.constants";
import { lazyLoad, Loadable } from "../../shared/utils/lazy-load";
import {
	getDefaultReportingPeriodValue,
	getReportingPeriodOptions,
	getReportingPeriodRange
} from "../../shared/utils/reporting-period";
import {
	formatDateForBE,
	formatNumberMaxDecimals,
	mapBase64ToBlob,
	saveAs
} from "../../shared/utils/utils";

enum PageStates {
	UPLOAD = "UPLOAD",
	LOADING = "LOADING",
	ERROR = "ERROR",
	MAIN = "MAIN"
}

type StrategicFilters = { fileId: string; startDate: string; endDate?: string };
type GeneralInformationKpis = {
	footprintValue: string | number;
	footprintUnit: string;
	countValue: string | number;
	countUnit: string;
};

@Component({
	selector: "globalictcc-strategic-applications",
	standalone: true,
	imports: [
		AsyncPipe,
		ButtonComponent,
		ChartComponent,
		DropdownComponent,
		InputComponent,
		KpiComponent,
		ReactiveFormsModule,
		SpinnerComponent,
		SwitchComponent,
		TableComponent,
		UploadFileComponent,
		PaginationComponent
	],
	templateUrl: "./strategic-applications.component.html",
	styleUrl: "./strategic-applications.component.scss"
})
export class StrategicApplicationsComponent implements OnInit, OnDestroy {
	@ViewChild("uploadReport") public uploadReportDialogContent: TemplateRef<unknown>;
	@ViewChild("rename") public renameDialogContent: TemplateRef<unknown>;

	public _PageStates = PageStates;
	public _pageState = signal<PageStates>(PageStates.LOADING);
	public readonly grants = GRANTS.swabl;
	public readonly permissions = RolePermission;

	public sameNameErrorMessage = signal<string>("");
	public uploadedFile = signal<File | undefined>(undefined);
	public isUploadDisabled = computed(() => !this.uploadedFile() || !!this.sameNameErrorMessage());
	public viewSwitchOptions: SelectOption[] = [
		{ label: "Baseline view", value: "upload" },
		{ label: "Realtime view", value: "realtime" }
	];

	public fileControl = new FormControl("", { nonNullable: true });
	public renameFileControl = new FormControl("");

	public filesOptions: SelectOption[] = [];
	public selectedFile = signal<string>("");

	public isExporting: boolean = false;

	public hostingChartColors = [COLORS.YELLOW_100, COLORS.TURQUOISE_50, COLORS.TURQUOISE_100];
	public scopeChartColors = [COLORS.TURQUOISE_100, COLORS.YELLOW_100];
	public applicationsChartOverrides: EChartsOption = {
		xAxis: {
			axisTick: {
				lineStyle: {
					color: COLORS.GREY_100
				}
			},
			splitLine: {
				show: true,
				lineStyle: {
					color: "rgba(148, 148, 148, 0.4)",
					type: "solid"
				}
			}
		},
		series: {
			barWidth: 24,
			itemStyle: {
				borderRadius: [2, 2, 2, 2]
			}
		}
	};

	public environmentalImpactCurrentPage: number = 0;
	public readonly environmentalImpactPageSize: number = 10;
	public environmentalImpactTotalItems: number = 0;
	public environmentalImpactTotalPages: number = 1;
	public environmentalImpactPageItems: number = 0;

	public environmentalImpactGridOptions: GridOptions = {
		columnDefs: [
			{ headerName: "Application", field: "application", flex: 4, maxWidth: 5000 },
			{ headerName: "# of layers", field: "numberOfLayers" },
			{
				headerName: "Emissions (kgCO2e)",
				field: "totalEmissions",
				valueFormatter: params => formatNumberMaxDecimals(params.value, 2)
			}
		],
		rowData: [],
		getRowStyle: params => {
			if (!params?.data?.total) return undefined;

			const percentage = (params.data.totalEmissions / params.data.total) * 100;

			return {
				background: `linear-gradient(to right, ${COLORS.TEXT_GRAY} ${percentage}%, transparent ${percentage}%)`
			};
		},
		onGridReady: this._onEnvironmentalImpactGridReady.bind(this)
	};

	public periodControl = new FormControl<ReportingPeriodValue>(getDefaultReportingPeriodValue(), {
		nonNullable: true
	});
	public periodOptions: SelectOption[] = getReportingPeriodOptions();

	private _tenantId: string;
	private _fileId?: string;
	private readonly _document: Document = inject(DOCUMENT);
	private _destroy$ = new Subject<void>();
	private _environmentalImpactGridApi?: GridApi;
	private readonly _statusPollingIntervalMs = 5000;
	private readonly _hostingTerminologyMap: Record<string, string> = {
		onpremise: "Local",
		"on-premise": "Local",
		"on premise": "Local",
		saas: "Outsourced",
		cloud: "Cloud"
	};
	private readonly _fiscalYearStartMonth = 3;

	private readonly _filters$ = new BehaviorSubject<StrategicFilters | undefined>(undefined);
	private readonly _totalEmissionsOvertimeRefresh$ = new Subject<void>();
	private readonly _hostingChartRefresh$ = new Subject<void>();
	private readonly _scopeChartRefresh$ = new Subject<void>();
	private readonly _applicationsChartRefresh$ = new Subject<void>();
	private readonly _environmentalImpactReload$ = new Subject<void>();
	private _latestEnvironmentalImpact?: Loadable<EnvironmentalImpactByApplication>;

	// Streams are cold: an API is only called while a rendered widget subscribes to it.
	public readonly generalInformation$: Observable<Loadable<GeneralInformationKpis>> = lazyLoad(
		this._filters$,
		filters =>
			this._strategicApplicationsService
				.getGeneralInformation(this._tenantId, filters.fileId)
				.pipe(
					map(response => ({
						footprintValue:
							formatNumberMaxDecimals(response.totalCo2Emission.value) ?? 0,
						footprintUnit: response.totalCo2Emission.unit ?? "",
						countValue: response.countApplications?.value ?? 0,
						countUnit: response.countApplications?.unit ?? ""
					}))
				)
	);

	public readonly totalEmissionsOvertimeChart$: Observable<Loadable<ChartResponse<LineChart>>> =
		lazyLoad(
			this._filters$,
			filters =>
				this._strategicApplicationsService.getTotalEmissionsOvertime(
					this._tenantId,
					filters.fileId,
					filters.startDate,
					filters.endDate
				),
			this._totalEmissionsOvertimeRefresh$
		);

	public readonly hostingChart$: Observable<Loadable<ChartResponse<PieChart>>> = lazyLoad(
		this._filters$,
		filters =>
			this._strategicApplicationsService
				.getEmissionByDeployment(this._tenantId, filters.fileId)
				.pipe(
					map(response =>
						this._remapPieChartSlices(response, this._hostingTerminologyMap)
					)
				),
		this._hostingChartRefresh$
	);

	public readonly scopeChart$: Observable<Loadable<ChartResponse<PieChart>>> = lazyLoad(
		this._filters$,
		filters =>
			this._strategicApplicationsService.getEmissionByScope(this._tenantId, filters.fileId),
		this._scopeChartRefresh$
	);

	public readonly applicationsChart$: Observable<Loadable<ChartResponse<BarChart>>> = lazyLoad(
		this._filters$,
		filters =>
			this._strategicApplicationsService.getEmissionApplications(
				this._tenantId,
				filters.fileId
			),
		this._applicationsChartRefresh$
	);

	public readonly environmentalImpact$: Observable<Loadable<EnvironmentalImpactByApplication>> =
		lazyLoad(
			this._filters$,
			filters =>
				this._strategicApplicationsService.getEnvironmentalImpactByApplication(
					filters.fileId,
					this.environmentalImpactCurrentPage,
					this.environmentalImpactPageSize
				),
			this._environmentalImpactReload$
		).pipe(tap(state => this._applyEnvironmentalImpact(state)));

	constructor(
		private _router: Router,
		private _strategicApplicationsService: StrategicApplicationsService,
		private _storageService: StorageService,
		private _dialogService: DialogService,
		private _toastService: ToastService,
		public authorizationService: AuthorizationService
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();
		this._fetchFiles().pipe(takeUntil(this._destroy$)).subscribe();
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	public onViewSwitch(targetView: string): void {
		if (targetView === "realtime") {
			this._router.navigateByUrl(`/${PATHS.DASHBOARD}/${PATHS.SOFTWARE_ASSETS}`);
		}
	}

	public onFileUpload(file: File): void {
		if (this.filesOptions.some(option => option.label === file.name)) {
			this.sameNameErrorMessage.set(
				"This name is already in use by another report. Please enter a different name."
			);
			return;
		}

		this.sameNameErrorMessage.set("");
		this.uploadedFile.set(file);
	}

	public onRemoveFile(): void {
		this.uploadedFile.set(undefined);
	}

	public onFileChange(fileId: string | string[]): void {
		if (Array.isArray(fileId)) {
			console.warn("Multiple file selection is not supported.");
			return;
		}

		if (fileId === this._fileId) {
			return;
		}

		this._fileId = fileId;

		const fileLabel = this.filesOptions.find(file => file.value === this._fileId)?.label;
		this.selectedFile.set(fileLabel ?? "");

		this._pageState.set(PageStates.LOADING);
		this._resetData();
		this._loadDataForSelectedFile();
	}

	public onUploadReport(): void {
		this._dialogService
			.open({
				data: {
					type: "confirm",
					confirmBtnText: "Upload",
					closeBtnText: "Cancel",
					title: "Upload report",
					content: this.uploadReportDialogContent,
					confirmAction: this.onConfirmUpload.bind(this),
					isConfirmActionDisabled: this.isUploadDisabled
				}
			})
			.afterClosed()
			.pipe(
				tap({
					next: () => {
						this.sameNameErrorMessage.set("");
						this.uploadedFile.set(undefined);
					}
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	public onRename(): void {
		if (!this._fileId) return;

		this.renameFileControl.setValue(this.selectedFile());
		this.sameNameErrorMessage.set("");

		this._dialogService
			.open({
				data: {
					type: "confirm",
					confirmBtnText: "Ok",
					closeBtnText: "Cancel",
					title: "Rename",
					content: this.renameDialogContent,
					confirmAction: this._onConfirmRename.bind(this)
				}
			})
			.afterClosed()
			.pipe(tap({ next: () => this.renameFileControl.reset() }), takeUntil(this._destroy$))
			.subscribe();
	}

	public onDelete(): void {
		if (!this._fileId) return;

		const fileName =
			this.filesOptions.find(file => file.value === this._fileId)?.label ||
			this.selectedFile();

		this._dialogService.open({
			data: {
				type: "confirm",
				confirmBtnText: "Yes, Delete",
				closeBtnText: "Cancel",
				title: `Are you sure you want to delete "${fileName}"?`,
				confirmAction: this._onConfirmDelete.bind(this)
			}
		});
	}

	public onRetry(): void {
		this._fetchFiles().pipe(takeUntil(this._destroy$)).subscribe();
	}

	public onApply(): void {
		this._loadDataForSelectedFile();
	}

	public isDownloadDisabled(): boolean {
		return !this._fileId || this.isExporting;
	}

	public onRenameChange(name: string | number | null): void {
		const value = typeof name === "string" ? name : (name?.toString() ?? "");
		const isAlreadyPresent = this.filesOptions.some(file => file.label === value);

		this.sameNameErrorMessage.set(
			isAlreadyPresent
				? "This name is already in use by another report. Please enter a different name."
				: ""
		);

		this.renameFileControl.setErrors(isAlreadyPresent ? { alreadyPresent: true } : null);
	}

	public refreshHostingChart(): void {
		this._hostingChartRefresh$.next();
	}

	public refreshScopeChart(): void {
		this._scopeChartRefresh$.next();
	}

	public refreshApplicationsChart(): void {
		this._applicationsChartRefresh$.next();
	}

	public refreshTotalEmissionsOvertimeChart(): void {
		this._totalEmissionsOvertimeRefresh$.next();
	}

	public refreshEnvironmentalImpactTable(): void {
		this.environmentalImpactCurrentPage = 0;
		this._environmentalImpactReload$.next();
	}

	public onEnvironmentalImpactNextPage(): void {
		this.environmentalImpactCurrentPage++;
		this._environmentalImpactReload$.next();
	}

	public onEnvironmentalImpactPreviousPage(): void {
		this.environmentalImpactCurrentPage--;
		this._environmentalImpactReload$.next();
	}

	public onDownloadFile(): void {
		if (!this._fileId || this.isExporting) return;

		this.isExporting = true;

		this._strategicApplicationsService
			.exportFile(this._tenantId, this._fileId)
			.pipe(
				takeUntil(this._destroy$),
				finalize(() => {
					this.isExporting = false;
				})
			)
			.subscribe({
				next: csvData => this._downloadCsv(csvData),
				error: error => {
					console.error("Error exporting Strategic Applications file:", error);
				}
			});
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	private _fetchFiles(): Observable<void> {
		this._pageState.set(PageStates.LOADING);

		return this._strategicApplicationsService.getFiles(this._tenantId).pipe(
			map(response => response.files ?? []),
			tap({
				next: files => {
					this.filesOptions = files.map(file => ({ label: file.name, value: file.id }));
					this._resetData();

					if (!this.filesOptions.length) {
						this._fileId = undefined;
						this.fileControl.setValue("", { emitEvent: false });
						this.selectedFile.set("");
						this._pageState.set(PageStates.UPLOAD);
						return;
					}

					if (
						!this._fileId ||
						!this.filesOptions.some(file => file.value === this._fileId)
					) {
						this._fileId = this.filesOptions[0]?.value;
					}

					const fileLabel = this.filesOptions.find(
						file => file.value === this._fileId
					)?.label;
					this.selectedFile.set(fileLabel ?? "");
					this.fileControl.setValue(this._fileId ?? "", { emitEvent: false });

					this._pageState.set(PageStates.MAIN);
					this._loadDataForSelectedFile();
				},
				error: () => this._pageState.set(PageStates.ERROR)
			}),
			map(() => void 0),
			finalize(() => {
				if (this._pageState() === PageStates.LOADING) {
					this._pageState.set(
						this.filesOptions.length ? PageStates.MAIN : PageStates.UPLOAD
					);
				}
			})
		);
	}

	private _loadDataForSelectedFile(): void {
		if (!this._fileId) return;

		const { start, end } = getReportingPeriodRange(this.periodControl.value);
		this._filters$.next({
			fileId: this._fileId,
			startDate: formatDateForBE(start)!,
			endDate: formatDateForBE(end)
		});

		setTimeout(() => {
			if (this._pageState() === PageStates.LOADING) {
				this._pageState.set(PageStates.MAIN);
			}
		}, 100);
	}

	private _onEnvironmentalImpactGridReady(params: GridReadyEvent): void {
		this._environmentalImpactGridApi = params.api;
		if (this._latestEnvironmentalImpact) {
			this._applyEnvironmentalImpact(this._latestEnvironmentalImpact);
		}
	}

	// The grid is driven through its API, so each state is pushed to it here.
	private _applyEnvironmentalImpact(state: Loadable<EnvironmentalImpactByApplication>): void {
		this._latestEnvironmentalImpact = state;

		const gridApi = this._environmentalImpactGridApi;
		if (!gridApi || gridApi.isDestroyed()) return;

		gridApi.setGridOption("loading", state.isLoading);
		if (state.isLoading) return;

		if (!state.data) {
			gridApi.setGridOption("rowData", []);
			return;
		}

		const total = state.data.totalEmission;
		gridApi.setGridOption(
			"rowData",
			state.data.contents.map(row => ({ ...row, total }))
		);
		this.environmentalImpactTotalItems = state.data.totalElements;
		this.environmentalImpactTotalPages = state.data.totalPages;
		this.environmentalImpactPageItems = state.data.contents.length;
	}

	public onConfirmUpload(): void {
		const file = this.uploadedFile();
		if (!file) return;

		this._pageState.set(PageStates.LOADING);

		const formData = new FormData();
		formData.append("file", file);

		this._strategicApplicationsService
			.uploadFile(this._tenantId, formData)
			.pipe(
				switchMap(response => this._pollFileStatus(response.fileId)),
				switchMap(statusResponse => {
					if (statusResponse.status === "DONE") {
						return this._fetchFiles();
					}

					return throwError(
						() => new Error(statusResponse.message || "File processing failed")
					);
				}),
				catchError(error => {
					this._pageState.set(PageStates.ERROR);
					console.error("Error processing Strategic Applications file:", error);
					return EMPTY;
				}),
				finalize(() => {
					this.uploadedFile.set(undefined);
				})
			)
			.subscribe();
	}

	private _onConfirmRename(): void {
		if (!this._fileId) return;

		const newName = this.renameFileControl.value;
		if (!newName) return;

		this._strategicApplicationsService
			.renameFile(this._tenantId, this._fileId, newName.toString())
			.pipe(
				switchMap(() => this._fetchFiles()),
				catchError(error => {
					console.error("Error renaming Strategic Applications file:", error);
					return EMPTY;
				})
			)
			.subscribe();
	}

	private _onConfirmDelete(): void {
		if (!this._fileId) return;

		this._pageState.set(PageStates.LOADING);

		this._strategicApplicationsService
			.deleteFile(this._tenantId, this._fileId)
			.pipe(
				switchMap(() => this._fetchFiles()),
				catchError(error => {
					this._pageState.set(PageStates.ERROR);
					console.error("Error deleting Strategic Applications file:", error);
					return EMPTY;
				})
			)
			.subscribe();
	}

	private _pollFileStatus(fileId: string): Observable<StrategicApplicationFileStatusResponse> {
		return timer(0, this._statusPollingIntervalMs).pipe(
			switchMap(() =>
				this._strategicApplicationsService.getFileStatus(this._tenantId, fileId)
			),
			filter(
				(response: StrategicApplicationFileStatusResponse) =>
					response.status === "DONE" || response.status === "ERROR"
			),
			take(1)
		);
	}

	private _resetData(): void {
		this._environmentalImpactGridApi?.setGridOption("rowData", []);
	}

	private _formatKpi(kpi: StrategicApplicationKpi): [string | number, string] {
		const numericValue = Number(kpi.value ?? 0);
		const value = formatNumberMaxDecimals(numericValue, 3, false);
		return [value, `CO2 ${kpi.unit ?? ""}`.trim()];
	}

	private _remapPieChartSlices(
		chartResponse: ChartResponse<PieChart>,
		labelMap: Record<string, string>
	): ChartResponse<PieChart> {
		if (!chartResponse?.chart?.PieChartSlice?.length) return chartResponse;

		const updatedSlices = chartResponse.chart.PieChartSlice.map(slice => {
			const key = slice.name?.toLowerCase().replace(/\s+/g, "") ?? "";
			const mappedName = labelMap[key] ?? slice.name;
			return { ...slice, name: mappedName };
		});

		return {
			...chartResponse,
			chart: {
				...chartResponse.chart,
				PieChartSlice: updatedSlices
			}
		};
	}

	private _downloadCsv(csvData: string): void {
		const blob = mapBase64ToBlob(csvData, "text/csv");
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		const fileName = this.selectedFile() || "strategic-applications";
		link.download = `${fileName}_emissions.csv`;
		link.click();
		window.URL.revokeObjectURL(url);
	}

	public onDownloadTemplate(): void {
		defer(() => {
			this._toastService.success(
				"Download started",
				"Both Level 1 and Level 2 templates are being downloaded."
			);
			return forkJoin([
				this._strategicApplicationsService.downloadTemplate("1"),
				this._strategicApplicationsService.downloadTemplate("2")
			]);
		})
			.pipe(takeUntil(this._destroy$))
			.subscribe({
				next: ([lv1Blob, lv2Blob]) => {
					saveAs(this._document, URL.createObjectURL(lv1Blob), "template_lv1.xlsx");
					saveAs(this._document, URL.createObjectURL(lv2Blob), "template_lv2.xlsx");
				}
			});
	}
}
